import _capitalize from 'lodash/capitalize';
import { isEmpty } from '@jjj/components';
import actions from './actions';
import { createReducer } from '../../../../helpers/reducer';
import { keyBy } from '../../../../helpers/core';
import { t } from '../../../../i18n';

const initState = {
  editForm: {},
  formInfo: {},
  formDataLoading: false,
  isEdit: false,
  loading: false,
  submitBtnLoading: false,
  position: null,
};

const initialEditData = {
  accept_applications: false,
  alt_name: '',
  hidden_on_job_boards: false,
  assessment: true,
  mgmt_access: false,
  name: '',
  passing_score: 0,
  typeform_id: '',
  skills: [
    {
      min_assessment_score: 0.0,
      min_reference_score: 0.0,
      position_score: 0.0,
      description: null,
      requirement_description: null,
      tasks_description: null,
    },
    {
      min_assessment_score: 0.0,
      min_reference_score: 0.0,
      position_score: 0.0,
      description: null,
      requirement_description: null,
      tasks_description: null,
    },
    {
      min_assessment_score: 0.0,
      min_reference_score: 0.0,
      position_score: 0.0,
      description: null,
      requirement_description: null,
      tasks_description: null,
    },
  ],
};

function validate(attr, value, state) {
  let error = '';

  if (attr === 'passing_score' && state.editForm.assessment && state.editForm.assessment.value) {
    if (value === 0) {
      error = t('dash.validation.non_zero_error', { fieldName: t('dash.admin.positions.passing_score_label') });
    }
  }

  if (attr === 'name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.admin.positions.columns.name_title') });
    }
  }

  if (attr === 'job_type_id') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.admin.positions.job_type_label') });
    }
  }

  return error;
}

function formatArray(payload, prop, state) {
  // eslint-disable-next-line no-use-before-define
  return payload[prop].map((item, index) => formatProp(payload[prop], index, state));
}

function formatObject(payload, prop, state) {
  const propsArray = Object.keys(payload[prop]);

  return keyBy(
    propsArray,
    name => name,
    // eslint-disable-next-line no-use-before-define
    name => formatProp(payload[prop], name, state),
  );
}

function formatProp(payload, prop, state) {
  const getProp = () => ({
    name: prop,
    value: payload[prop],
    error: validate(prop, payload[prop], state),
    touched: false,
  });

  if (Array.isArray(payload[prop])) {
    return formatArray(payload, prop, state);
  }

  if (!Array.isArray(payload[prop]) && typeof payload[prop] === 'object' && payload[prop] != null) {
    return formatObject(payload, prop, state);
  }

  return getProp();
}

function onLoad(state) {
  return { ...state, loading: true, isEdit: false };
}

function onLoadSuccess(state, { payload }) {
  return { ...state, ...payload.data, loading: false };
}

// Only the create form gets `job_type_id`; on edit an empty required field would block saving.
const initialCreateData = { ...initialEditData, job_type_id: null };

function onCreate(state) {
  return {
    ...state,
    isEdit: true,
    position: initialCreateData,
    editForm: formatProp({ editForm: initialCreateData }, 'editForm', state),
    loading: false,
  };
}

function onEdit(state, { payload }) {
  const position = { ...initialEditData, ...payload.position };
  const propsArray = Object.keys(position);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => formatProp(position, prop, state),
  );

  return { ...state, isEdit: true, editForm, loading: false };
}

function onAdd(state) {
  return { ...state, submitBtnLoading: true };
}

function onChangeProperty(state, { payload }) {
  const { attr, value, silent } = payload;
  const { editForm } = state;

  editForm[attr] = silent
    ? value
    : {
        value,
        error: validate(attr, value, state),
        name: attr,
        touched: true,
      };

  return { ...state, editForm: { ...editForm } };
}

function onChangeNestedProperty(state, { payload }) {
  const { attrs, value } = payload;
  const editForm = { ...state.editForm };

  if (attrs.length > 2) {
    let data = null;
    const name = attrs[attrs.length - 1];
    for (let i = 0; i < attrs.length - 1; i += 1) {
      data = (data || editForm)[attrs[i]];
    }

    data[name] = {
      name: attrs.join('.'),
      touched: true,
      error: validate(attrs.join('.'), value, state),
      value,
    };
  } else {
    if (editForm[attrs[0]] === undefined) {
      editForm[attrs[0]] = {};
    }
    editForm[attrs[0]][attrs[1]] =
      typeof value === 'object' && value != null
        ? keyBy(
            Object.keys(value),
            prop => prop,
            prop => formatProp(value, prop, state),
          )
        : {
            name: attrs.join('.'),
            touched: true,
            error: validate(attrs.join('.'), value, state),
            value,
          };
  }

  return { ...state, editForm };
}

function onSaveSuccess(state) {
  return { ...state, isEdit: false, loading: true, submitBtnLoading: false, editForm: {} };
}

function getDataWithErrors(editForm, result) {
  // eslint-disable-next-line no-restricted-syntax
  for (const key in result) {
    if (key === 'skills') {
      const error = result[key];
      // eslint-disable-next-line no-restricted-syntax
      for (const index in error) {
        if (Object.prototype.hasOwnProperty.call(error, index)) {
          // eslint-disable-next-line no-restricted-syntax
          for (const subKey in error[index]) {
            if (Object.prototype.hasOwnProperty.call(error[index], subKey)) {
              editForm.skills[index][subKey].error = _capitalize(error[index][subKey][0]);
              editForm.skills[index][subKey].touched = true;
            }
          }
        }
      }
    } else if (editForm[key]) {
      editForm[key].error = _capitalize(result[key][0]);
      editForm[key].touched = true;
    }
  }

  return editForm;
}

function onSave(state) {
  return { ...state, submitBtnLoading: true };
}

function onSaveFail(state, { payload }) {
  return {
    ...state,
    isEdit: true,
    loading: false,
    editForm: getDataWithErrors({ ...state.editForm }, payload.data),
    submitBtnLoading: false,
  };
}

function onCancel(state) {
  return { ...state, isEdit: false, editForm: {} };
}

function onLoadFormData(state) {
  return { ...state, formDataLoading: true };
}

function onLoadFormDataSuccess(state, { payload }) {
  return { ...state, formInfo: { ...state.formInfo, ...payload }, formDataLoading: false };
}

function onLoadFormDataError(state) {
  return { ...state, formDataLoading: false };
}

// Marks every leaf touched, which is what reveals the errors already sitting on the fields.
function touchAll(node) {
  if (Array.isArray(node)) {
    return node.map(touchAll);
  }

  if (node && typeof node === 'object') {
    if (Object.prototype.hasOwnProperty.call(node, 'value')) {
      return { ...node, touched: true };
    }

    return keyBy(
      Object.keys(node),
      key => key,
      key => touchAll(node[key]),
    );
  }

  return node;
}

function onTouch(state) {
  return { ...state, editForm: touchAll(state.editForm) };
}

function onClearStore() {
  return { ...initState };
}

export default createReducer(initState, {
  [actions.ADD]: onAdd,
  [actions.CANCEL]: onCancel,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHANGE_NESTED_PROPERTY]: onChangeNestedProperty,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.CREATE]: onCreate,
  [actions.EDIT]: onEdit,
  [actions.LOAD]: onLoad,
  [actions.LOAD_FORM_DATA]: onLoadFormData,
  [actions.LOAD_FORM_DATA_ERROR]: onLoadFormDataError,
  [actions.LOAD_FORM_DATA_SUCCESS]: onLoadFormDataSuccess,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.SAVE]: onSave,
  [actions.SAVE_FAIL]: onSaveFail,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.TOUCH]: onTouch,
});
